import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef, useMemo } from "react";
import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import { getGroupChatsFromLocalDB } from "@/indexedDB/services/chats";

import NewChatMenu from "../NewChatMenu";
import SearchChatInput from "../SearchChatInput";
import { ChatUserAction } from "@/zustand/NavigationState";
import ChatListEntry, { CHAT_LIST_ITEM_HEIGHT } from "../ChatListEntry";
import ArchivedChatsSection from "../ArchivedChatsSection";
import { useTranslation } from "react-i18next";

/**
 * Purpose: Displays group chats with separate sections for regular and archived chats
 *
 * How it works:
 * 1. Fetches group chats from local DB
 * 2. Filters chats into regular and archived categories
 * 3. Displays regular chats in a virtualized list
 * 4. Shows archived chats in a collapsible section
 *
 * Key features:
 * - Pinned chats appear at the top of the regular chats list
 * - Archived chats are shown in a separate collapsible section
 * - Empty state handling when no chats exist
 * - Chat list is virtualized for performance
 */
export default function GroupChatsListView() {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement>(null);

  const chats = useSafeLiveQuery(() => getGroupChatsFromLocalDB(), []);

  const { sortedRegularChats, archivedChats } = useMemo(() => {
    const regular = chats?.filter((chat) => !chat.isArchived) || [];
    const archived = chats?.filter((chat) => chat.isArchived) || [];

    const sorted = [...regular].sort((a, b) => {
      if (a.isPinned === b.isPinned) {
        return 0;
      }
      return a.isPinned ? -1 : 1;
    });

    return { sortedRegularChats: sorted, archivedChats: archived };
  }, [chats]);

  const rowVirtualizer = useVirtualizer({
    count: sortedRegularChats.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CHAT_LIST_ITEM_HEIGHT,
    overscan: 5,
  });

  if (!chats) {
    return null;
  }

  return (
    <div className="relative flex size-full flex-col overflow-hidden">
      <div className="flex h-leftColumnHeaderHeight shrink-0 items-center border-b border-primary/20 px-4">
        <h1 className="text-lg font-semibold">{t("groupChats")}</h1>
        <NewChatMenu />
      </div>

      <SearchChatInput searchAction={ChatUserAction.SEARCH_GROUP_CHAT} />

      {sortedRegularChats.length === 0 && archivedChats.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center">
          <h2 className="text-center text-lg font-semibold">{t("noChats")}</h2>
          <p className="text-center text-sm text-muted-2">
            {t("noChatsDescription")}
          </p>
        </div>
      )}

      <div ref={parentRef} className="flex-1 overflow-y-auto p-2">
        {sortedRegularChats.length > 0 && (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const chat = sortedRegularChats[virtualRow.index];
              return (
                <div
                  key={chat.id}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <ChatListEntry chat={chat} />
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Archived chats section */}
      <ArchivedChatsSection archivedChats={archivedChats} />
    </div>
  );
}
